import React, { useEffect, useState } from 'react';
import { useNotificationStore } from '../../stores/notificationStore';
import { useAuthStore } from '../../stores/authStore';
import { Alert } from '../ui/Alert/Alert';
import { Button } from '../ui/Button/Button';

function useAutoDismiss(id: string | null, duration?: number | null) {
	useEffect(() => {
		if (!id || !duration || duration <= 0) return;
		const t = setTimeout(() => {
			useNotificationStore.getState().dismiss(id);
		}, duration);
		return () => clearTimeout(t);
	}, [id, duration]);
}

export function Notifications() {
	const notifications = useNotificationStore((s) => s.notifications);
	const authError = useAuthStore(s => s.error);
	return (
		<>
			{authError?.includes('Too Many Requests') && (
				<div className="fixed top-2 left-0 right-0 z-[1100] flex justify-center pointer-events-none">
					<Alert 
						variant="warning" 
						className="pointer-events-auto shadow-lg"
					>
						<div className="flex items-center gap-3">
							<span>You're sending requests too quickly. Please slow down.</span>
							<Button 
								size="sm" 
								variant="outline"
								onClick={() => window.location.reload()}
							>
								Reset
							</Button>
						</div>
					</Alert>
				</div>
			)}
			<div className="fixed bottom-4 left-4 z-[1000] flex flex-col gap-2 w-[360px]" aria-live="polite" aria-atomic="true">
				{notifications.map((n) => (
					<Toast key={n.id} id={n.id} type={n.type} title={n.title} message={n.message} duration={n.duration} />
				))}
			</div>
		</>
	);
}

function Toast({ id, type, title, message, duration }: { id: string; type: 'success' | 'error' | 'info' | 'warning'; title?: string; message: string; duration?: number | null; }) {
	const [visible, setVisible] = useState(false);
	useEffect(() => { const f = requestAnimationFrame(() => setVisible(true)); return () => cancelAnimationFrame(f); }, []);
	useAutoDismiss(id, duration);
	const dismiss = () => useNotificationStore.getState().dismiss(id);

	const variantMap = {
		success: 'success' as const,
		error: 'error' as const,
		info: 'info' as const,
		warning: 'warning' as const,
	};

	return (
		<div className={`transform transition-all duration-300 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
			<Alert 
				variant={variantMap[type] || 'info'}
				title={title}
				onClose={dismiss}
				className="shadow-lg"
			>
				{message}
			</Alert>
		</div>
	);
} 